/**
 * Sélecteur de disponibilités candidat — refonte v2 (feedback Kayla 14-07).
 *
 * Geste PRINCIPAL simplifié : le candidat tape un shift (A→F) → il est
 * sélectionné pour TOUS les jours d'un coup. Il peut ensuite AJUSTER jour par
 * jour (retirer/ajouter des journées) dans la grille en dessous. Re-taper un
 * shift déjà complet le retire de tous les jours.
 *
 * Modèle : les 6 VRAIS shifts fixes A→F (source de vérité `SHIFTS` dans
 * `constants.ts`, mêmes heures que `cockpit/panels/organisation/ShiftsTab.tsx`,
 * ancrées en `SHIFT_BASE_TIMEZONE`). La lettre stockée (`a`..`f`) est
 * INDÉPENDANTE du fuseau ; seul l'AFFICHAGE des heures est localisé dans le
 * fuseau du candidat (`timezone`), via offset-diff.
 *
 * Boutons `aria-pressed` — PAS `<Checkbox>` (bug « cases qui ne se cochent
 * pas ») : les créneaux sont des `Button` à deux lignes (la marque sur celui
 * qui est pris sur tous les jours), l'ajustement par jour des `Button` en
 * pilule posés dans des `Lignes` — UN seul style de bouton dans tout l'écran. Cibles larges pour mobile (≥ 44 px).
 *
 * `Temps.Creneau` / `PlanningType` / `Semaine` ne remplacent pas la matrice :
 * ils DISENT un horaire, ils n'en font pas une grille jour × créneau qu'on
 * allume (le `Creneau` cliquable porte `aria-current`, pas `aria-pressed`, et
 * écrit « 11:00 – 17:00 · 6 h »). Les tests tiennent ces boutons `aria-pressed`.
 */
import { useMemo } from "react";

import { Badge, Button, Carte, Grille, Ligne, Lignes } from "@/shared/ui";
import { Check } from "@/shared/ui/icons";
import { minutesToHHMM } from "@/shared/shiftTime";
import { zoneOffsetMinutes } from "@/shared/timezone";

import { DAY_LABELS, DAY_LABELS_FULL, SHIFTS_RECRUTES, SHIFT_BASE_TIMEZONE } from "../constants";
import { DAY_KEYS, type Availability, type DayKey, type SlotKey } from "../types";

interface ShiftAvailabilityPickerProps {
  value: Availability;
  onChange: (next: Availability) => void;
  /** Fuseau IANA du candidat — pilote UNIQUEMENT l'affichage des heures. */
  timezone: string;
}

interface LocalizedShift {
  key: SlotKey;
  letter: string;
  name: string;
  localStart: number;
  localEnd: number;
}

/**
 * Convertit les shifts RECRUTÉS (ancrés `SHIFT_BASE_TIMEZONE`) vers les heures
 * que verrait le candidat dans `timezone` — offset-diff à l'instant présent
 * (suffisant pour un affichage ; pas de garantie DST par jour, cf.
 * `convertSlotToZone` si un jour précis devait un jour être affiché).
 * Recrutés, pas tous : le 03–09 est dans le planning mais pas dans l'annonce
 * (cf. `SHIFTS_RECRUTES`).
 */
function localizeShifts(timezone: string): LocalizedShift[] {
  const now = Date.now();
  const diff =
    zoneOffsetMinutes(now, timezone) - zoneOffsetMinutes(now, SHIFT_BASE_TIMEZONE);
  return SHIFTS_RECRUTES.map((s) => ({
    key: s.key,
    letter: s.letter,
    name: s.name,
    localStart: (((s.startMin + diff) % 1440) + 1440) % 1440,
    localEnd: (((s.endMin + diff) % 1440) + 1440) % 1440,
  }));
}

const TOTAL_DAYS = DAY_KEYS.length;

export function ShiftAvailabilityPicker({
  value,
  onChange,
  timezone,
}: ShiftAvailabilityPickerProps) {
  const localizedShifts = useMemo(() => localizeShifts(timezone), [timezone]);

  /** Nombre de jours où un shift est coché (0..7). */
  function dayCount(shift: SlotKey): number {
    return DAY_KEYS.reduce(
      (n, day) => n + ((value[day] ?? []).includes(shift) ? 1 : 0),
      0,
    );
  }

  /**
   * Tape un shift : le pose sur TOUS les jours ; s'il y est déjà partout, il
   * l'enlève de tous.
   *
   * 🩸 CUMULATIF, et ça ne l'était pas. Kayla, 18-08 : « il y en a peut-être
   * qui sont dispo tout le temps, donc qu'il puisse sélectionner tous les
   * shifts. » Les deux gestes de ce composant REMPLAÇAIENT la sélection du
   * jour (`next[day] = [shift]`) : un candidat disponible matin ET soir ne
   * pouvait déclarer que l'un des deux, et cliquer le second effaçait le
   * premier en silence. On ne perdait pas une préférence de confort — on
   * perdait de la disponibilité, c'est-à-dire la seule chose que ce champ
   * sert à collecter.
   */
  function toggleAllDays(shift: SlotKey) {
    const turnOn = dayCount(shift) < TOTAL_DAYS;
    const next: Availability = { ...value };
    for (const day of DAY_KEYS) {
      const current = next[day] ?? [];
      next[day] = turnOn
        ? current.includes(shift)
          ? current
          : [...current, shift]
        : current.filter((x) => x !== shift);
    }
    onChange(next);
  }

  /** Ajuste une journée — plusieurs shifts par jour. Re-cliquer un shift actif
   *  l'enlève ; cliquer un autre l'AJOUTE à côté, il ne le remplace plus. */
  function toggleOneDay(day: DayKey, shift: SlotKey) {
    const current = value[day] ?? [];
    onChange({
      ...value,
      [day]: current.includes(shift)
        ? current.filter((x) => x !== shift)
        : [...current, shift],
    });
  }

  return (
    <div className="flex flex-col gap-3">
      {/* ── Geste principal : tape un shift → tous les jours ───────────────
          `min` 160 : deux créneaux par rangée, du téléphone au bureau. */}
      <Grille min={160} ecart={8}>
        {localizedShifts.map((s) => {
          const count = dayCount(s.key);
          const all = count === TOTAL_DAYS;
          const partial = count > 0 && !all;
          return (
            <Button
              key={s.key}
              type="button"
              size="lg"
              variant={all ? "accent" : "secondary"}
              aria-pressed={count > 0}
              onClick={() => toggleAllDays(s.key)}
              aria-label={`Shift ${s.letter} ${s.name}, ${
                all
                  ? "tous les jours"
                  : partial
                    ? `${count} jour(s) sur 7`
                    : "aucun jour"
              } — taper pour ${all ? "tout retirer" : "mettre tous les jours"}`}
              className="relative h-auto min-h-[76px] flex-col items-start justify-center gap-1 px-4 py-3 text-left"
            >
              <span className="pr-8 text-[15px] font-bold leading-tight">
                {s.letter} · {s.name}
              </span>
              <span className="text-[13px] font-normal tabular-nums opacity-75">
                {minutesToHHMM(s.localStart)}–{minutesToHHMM(s.localEnd)}
              </span>
              {/* Pastille « N/7 » quand seulement certains jours sont cochés ; la coche quand ils le sont tous. */}
              {partial && (
                <Badge ton="marque" className="absolute right-2.5 top-2.5 tabular-nums">
                  {count}/7
                </Badge>
              )}
              {all && <Check aria-hidden weight="bold" className="absolute right-3 top-3 h-4 w-4" />}
            </Button>
          );
        })}
      </Grille>
      <p className="text-[13px] leading-snug text-muted">
        Tape un shift : il se met sur tous les jours. Tu peux en cocher
        plusieurs le même jour — coche tout ce que tu peux faire. Les heures
        sont à ton fuseau.
      </p>

      {/* ── Ajustement fin par jour (optionnel) ──────────────────────────── */}
      <Carte remplissage="aucun" className="pt-3.5">
        <p className="px-4 pb-1 text-[13px] font-medium leading-snug text-muted">
          Ajuste par jour si besoin (retire ou ajoute une journée)
        </p>
        <Lignes>
          {DAY_KEYS.map((day) => (
            <Ligne
              key={day}
              etiquette={DAY_LABELS[day]}
              className="!px-3"
              valeur={
                <span
                  className="grid gap-1.5"
                  style={{ gridTemplateColumns: `repeat(${localizedShifts.length}, minmax(0, 1fr))`, width: "min(15rem, 62vw)" }}
                >
                  {localizedShifts.map((s) => {
                    const on = (value[day] ?? []).includes(s.key);
                    return (
                      <Button
                        key={s.key}
                        size="md"
                        variant={on ? "accent" : "secondary"}
                        aria-pressed={on}
                        onClick={() => toggleOneDay(day, s.key)}
                        aria-label={`${DAY_LABELS_FULL[day]} · ${s.letter} ${s.name}`}
                        className="h-11 min-w-0 px-0 text-[14px]"
                      >
                        {s.letter}
                      </Button>
                    );
                  })}
                </span>
              }
            />
          ))}
        </Lignes>
      </Carte>
    </div>
  );
}
